<template>
  <div
    id="app"
    class="h-full"
    :class="{ 'user-has-tabbed': userHasTabbed }">
    <MaintenanceScreen
      v-if="featureEnabled('planned-maintenance')"
      class="h-full flex items-center justify-center"/>
    <TWLoadingSpinner v-else-if="loading || !initialised" />
    <div
      v-else
      class="flex flex-col h-full"
      data-test="main-screen">
      <AppLevelBannerContainer
        v-if="showAppLevelBanner"
        class="flex-grow-0"
      />
      <div class="flex-grow-0">
        <SignableHeader
          v-if="$router.currentRoute.meta.requiresAuth && $store.getters['auth/isAuthenticated']"
          :navButton="slideToggle"
          :windowWidth="windowWidth"
          :sideMenuActive="menuIsToggled"
          :pageHeader="$t(`routeTitles.${$route.meta.title}`)"
          :pageSubHeader="$t(`subHeader.${$route.meta.title}`)"
          :dropdownMenu="enabledDropdownMenuItems"
          :user="$store.state.user.user"
        />
      </div>
      <div class="flex-grow min-h-0 flex flex-col">
        <div class="flex-grow overflow-hidden flex flex-row items-stretch">
          <div>
            <SignableSideMenu
              v-if="$router.currentRoute.meta.requiresAuth && $store.getters['auth/isAuthenticated']"
              class="flex-grow-0 w-menuWidth absolute xl:relative"
              ref="side-navigation"
              :key="`side-navigation-${$i18n.locale}`"
            />
          </div>
          <div class="w-full min-w-0">
            <div
              ref="scrollRef"
              class="router overflow-y-auto overflow-x-hidden relative h-full"
              :class="[$router.currentRoute.meta.requiresAuth && $store.getters['auth/isAuthenticated'] ? 'p-twentyFive' : 'p-base', {'router-blurred': showMenu && menuIsToggled, 'document-edit': $router.currentRoute.name === 'TemplateEdit' || $router.currentRoute.name === 'EnvelopeUpdate'}, {'p-0': $router.currentRoute.name === 'EnvelopesStepAddFields'}] ">
              <div
                v-if="showMenu && menuIsToggled"
                class="blur h-full w-full top-0 right-0 absolute background-overlay"
                @click="slideToggle">
              </div>
              <RouterView
                class="h-full"
                ref="scroll-element"
                id="o-router">
              </RouterView>
            </div>
          </div>
        </div>
      </div>
    </div>
    <VNotifications />
    <LogoutModal
      :show="showLogoutModal"
      @close="closeLogoutModal"
    />
  </div>
</template>

<script>
import AppLevelBannerContainer from '@/views/messages/AppLevelBannerContainer.vue';
import TWLoadingSpinner from '@/components/tailwind/LoadingSpinner.vue';
import SignableSideMenu from '@/views/components/SignableSideMenu.vue';
import VNotifications from '@/components/Notifications.vue';
import { mapActions, mapGetters } from 'vuex';
import Router from 'vue-router';
import SignableHeader from '@/views/components/SignableHeader.vue';
import LogoutModal from '@/components/ui/user/modals/LogoutModal';
import { MaintenanceScreen } from '@/components/screens/MaintenanceScreen';

export default {
  name: 'app',
  components: {
    SignableHeader,
    TWLoadingSpinner,
    SignableSideMenu,
    VNotifications,
    AppLevelBannerContainer,
    LogoutModal,
    MaintenanceScreen,
},
  data() {
    return {
      windowWidth: null,
      menuIsToggled: false,
      showSendEnvelope: true,
      loading: false,
      userHasTabbed: false,
      showAppLevelBanner: true,
      showLogoutModal: false,
    };
  },
  created() {
    // Set the feature flags once Launch darkly is ready
    this.$launchDarkly.on('ready', this.launchDarklyListener);
    // Listen for changes to featureFlags in LaunchDarkly and then set the updated features in the store
    this.$launchDarkly.on('change', this.launchDarklyListener);

    if (window.location.hash && window.location.pathname === '/') { // TEMP Detect if hash mode style route has been provided and redirect accordingly
      const path = window.location.hash.slice(1, window.location.hash.length); // Removes hash from start of hash fragment
      this.$router.push({ path, hash: null }) // Navigate to path as derived from hash fragment
        .catch((error) => {
          if (error.type === Router.NavigationFailureType.redirected) {
            // if a user is logged out when we try to redirect them the auth guard will redirect to '/login' causing vue-router to throw this error
            // it's not a problem so do nothing
            return;
          }

          throw error;
        });
    }

    this.$bus.$on('globalLoader', (loading) => {
      this.loading = loading;
    });
    window.addEventListener('resize', this.handleResizeLayout);

    this.$router.afterEach(() => {
      if (this.$refs.scrollRef) {
        this.$refs.scrollRef.scrollTop = 0;
      }
    });
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResizeLayout);
  },
  mounted() {
    this.windowWidth = window.innerWidth;

    this.initialiseHelpScout();

    window.addEventListener('keydown', this.handleTabPress);
  },
  methods: {
    ...mapActions({
      setFlags: 'featureFlags/SET_FLAGS',
      setNotifications: 'notifications/SET',
    }),
    launchDarklyListener() {
      this.setFlags(this.$launchDarkly.allFlags());
      this.setNotifications(!this.featureEnabled('planned-maintenance'));
    },
    initialiseHelpScout() {
      /* Start helpscout Snippet */
      /* eslint-disable */
      !function (e, t, n) {
        function a() {
          var e = t.getElementsByTagName('script')[0],
              n = t.createElement('script');
          n.type = 'text/javascript';
          n.async = !0;
          n.src = 'https://beacon-v2.helpscout.net';
          e.parentNode.insertBefore(n, e);
        }

        if (e.Beacon = n = function (t, n, a) {
          e.Beacon.readyQueue.push({
            method: t,
            options: n,
            data: a
          });
        }, n.readyQueue = [], 'complete' === t.readyState) {
          return a();
        }
        e.attachEvent ? e.attachEvent('onload', a) : e.addEventListener('load', a, !1);
      }(window, document, window.Beacon || function () {});

      window.Beacon('init', process.env.VUE_APP_HELPSCOUT_CHAT_UUID)
      /* eslint-enable */
      /* End helpscout Snippet */
    },
    handleResizeLayout() {
      this.windowWidth = window.innerWidth;
      this.menuIsToggled = false;
    },
    slideToggle() {
      this.menuIsToggled = !this.menuIsToggled;
    },
    handleTabPress(e) {
      if (e.keyCode === 9) {
        this.userHasTabbed = true;
        window.removeEventListener('keydown', this.handleTabPress);
      }
    },
    openLogoutModal() {
      this.showLogoutModal = true;
    },
    closeLogoutModal() {
      this.showLogoutModal = false;
    },
    getOffset(el) {
      const element = el.getBoundingClientRect();
      return element.top + (window.scrollY || window.pageYOffset);
    },
  },
  watch: {
    $route(to) {
      this.menuIsToggled = false;

      if (to.meta.hideAppLevelBanner !== undefined) {
        this.showAppLevelBanner = false;
      } else {
        this.showAppLevelBanner = true;
      }
    },
    user(newVal) {
      if (newVal) {
        // This switches the user out from an anonymous user to the authenticated user
        this.$launchDarkly.identify({
            key: newVal.company_id,
            kind: 'user',
        });
      } else {
        this.$launchDarkly.identify({
          key: 'anonymous-user',
        });
      }
    },
  },
  computed: {
    ...mapGetters({
      initialised: 'initialised',
      user: 'user/user',
      userHasRole: 'roles/userHasRole',
      featureEnabled: 'featureFlags/featureEnabled',
    }),
    dropdownMenuItems() {
      return [
        {
          title: this.$t('pageHeader.dropdownMenu.myAccount'), route: '/my-profile', ec: 'header', et: 'menu_my_account',
        },
        {
          title: this.$t('pageHeader.dropdownMenu.companySettings'), route: '/company-settings', ec: 'header', et: 'menu_company_settings', roles: ['super-admin'],
        },
        {
          title: this.$t('pageHeader.dropdownMenu.help'), route: '/help', ec: 'header', et: 'menu_help',
        },
        {
          title: this.$t('pageHeader.dropdownMenu.logout'), action: this.openLogoutModal, ec: 'header', et: 'menu_logout',
        },
      ];
    },
    showMenu() {
      if (this.windowWidth && this.windowWidth >= 1020) return true;
      return !!(this.menuIsToggled);
    },
    // TODO : look at extracting this into a util?
    enabledDropdownMenuItems() {
      return this.dropdownMenuItems.filter((menuItem) => {
        if (menuItem.roles === undefined) return true; // if no role requirements set, display

        // Array intersection between needed roles for menu item to be shown & roles we have
        return this.userHasRole(menuItem.roles);
      });
    },
  },
};
</script>

<style scoped>
  .router {
    background: #f8f8f8;
    transition: all .15s ease;
    box-shadow: 10px 0 30px rgba(0, 0, 0, 0.25);
  }

  .router-blurred {
    position: fixed;
    transform: translateX(260px);
    left: 0;
    padding: 25px;
  }

  .document-edit {
    padding: 0;
  }

  .background-overlay {
    bottom: 0;
    right: 0;
    background: rgba(248, 248, 248, 0.5);
    z-index: 100;
  }

  .slide-fade-enter-active {
    transition: all .4s ease;
  }
  .slide-fade-leave-active {
    transition: all .1s ease;
  }
  .slide-fade-enter, .slide-fade-leave-to {
    opacity: 0;
  }
</style>
